import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/react-router'
import type { UserCapability } from '@joyful/utils'
import { RawQueryState } from '../../core/components/QueryState'
import { CurrentUserContext } from '../utils/currentUserContext'
import { useHasUserCapability } from '../hooks/useHasUserCapability'
import { useHasFeatureFlag } from '../hooks/useHasFeatureFlag'
import type { FeatureFlag } from '../utils/featureFlags'

export interface CapabilityProtectedRouteProps {
  capability: UserCapability | Array<UserCapability>
  component: React.ComponentType
  featureFlag?: FeatureFlag | Array<FeatureFlag>
  match?: 'all' | 'any'
}

export function CapabilityProtectedRoute({
  capability,
  component: Component,
  featureFlag,
  match = 'any',
}: CapabilityProtectedRouteProps) {
  const navigate = useNavigate()
  const auth = useUser()

  const currentUser = CurrentUserContext.useSafeContext()
  const checkCapability = useHasUserCapability()
  const checkFeatureFlag = useHasFeatureFlag()
  const capabilities = Array.isArray(capability) ? capability : [capability]
  const featureFlags = Array.isArray(featureFlag)
    ? featureFlag
    : [featureFlag].filter(Boolean)
  const hasRequiredCapability =
    match === 'all'
      ? capabilities.every((cap) => {
          return checkCapability(cap)
        })
      : capabilities.some((cap) => {
          return checkCapability(cap)
        })
  const hasRequiredFeatureFlag = featureFlags.every((flag) => {
    return checkFeatureFlag(flag)
  })
  const hasRequiredAccess =
    hasRequiredCapability === true && hasRequiredFeatureFlag === true

  const isUserDataLoaded = auth.isLoaded === true && currentUser !== null

  useEffect(() => {
    if (isUserDataLoaded && hasRequiredAccess === false) {
      void navigate('/')
    }
  }, [navigate, hasRequiredAccess, isUserDataLoaded])

  return (
    <RawQueryState
      isPending={isUserDataLoaded === false}
      isSuccess={isUserDataLoaded === true}
      isError={false}
      loading={<div>Loading...</div>}
    >
      {hasRequiredAccess === true ? <Component /> : <div>Not authorized</div>}
    </RawQueryState>
  )
}
